/**
 * Authentication Service
 * Provides Firebase Authentication functionality for the application.
 */
import { Injectable, type OnDestroy } from '@angular/core';
// import { browserLocalPersistence } from 'firebase/auth';
import type { Unsubscribe } from 'firebase/auth';
import { 
    Auth, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword,
    signOut, 
    sendPasswordResetEmail,
    fetchSignInMethodsForEmail,
    onAuthStateChanged,
    setPersistence,
    signInWithCustomToken,
    getAuth,
} from '@angular/fire/auth';
import type { User } from 'firebase/auth';
import { FirebaseApp, getApp, initializeApp } from '@angular/fire/app';

import { environment } from '@shared/environment';
import { ConnectivityService } from '../lib/connectivity.service';
import type { TLoginCredentials } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class AuthService implements OnDestroy {
    private AuthMode = {
        UNKNOWN: 0,
        AUTHENTICATE_LOGIN_MODE: 1,
        REAUTHENTICATE_LOGIN_MODE: 2,
        LOG_OUT_MODE: 3,
        REGISTER_NEW_USER_MODE: 4,
        UPDATE_PASSWORD_MODE: 5,
        DELETE_USER_MODE: 6
    };

    private currentMode = this.AuthMode.UNKNOWN;
    private secondaryApp: FirebaseApp | null = null;
    private secondaryAuth: Auth | null = null;
    private authStateUnsubscribe: Unsubscribe | null = null;

    constructor(
        public auth: Auth, 
        private connectivityService: ConnectivityService
    ) {
        // Set up auth state listener once during service initialization
        this.authStateUnsubscribe = onAuthStateChanged(this.auth, 
            (user) => this.myOnAuthStateChanged(user)
        );
    }

    public ngOnDestroy(): void {
        // Clean up listener when service is destroyed
        if (this.authStateUnsubscribe) {
            this.authStateUnsubscribe();
        }
    }

    /**
     * Lazy initialization of secondary auth
     * Only creates the secondary app when needed
     */
    private getSecondaryAuth(): Auth {
        if (!this.secondaryAuth) {
            try {
                // Try to get existing secondary app first
                this.secondaryApp = getApp('Secondary');
            } catch {
                // If it doesn't exist, create it
                this.secondaryApp = initializeApp(environment.firebase, 'Secondary');
            }
            this.secondaryAuth = getAuth(this.secondaryApp);
        }
        return this.secondaryAuth;
    }

    public async login(email: string, password: string): Promise<TLoginCredentials> {
        try {
            // Set persistence before signing in
            // const { browserLocalPersistence } = await import('firebase/auth');
            // await setPersistence(this.auth, browserLocalPersistence);

            const cred = await signInWithEmailAndPassword(this.auth, email, password);
            const user = cred.user;
            
            if (!user) {
                throw new Error('User not found');
            }
            
            const credentials: TLoginCredentials = { 
                'email': user.email || '', 
                'userUid': user.uid 
            };
            
            await this.dumpUserClaims();
            return credentials;
        } catch (err) {
            let errorMessage = 'Unknown Error';

            if (err instanceof Error) {
                errorMessage = err.message;
                
                // Firebase error codes use 'auth/' prefix
                if (errorMessage.includes('auth/wrong-password') || 
                    errorMessage.includes('auth/user-not-found')) {
                    errorMessage = 'This email address is not registered or the password is invalid.';
                } else if (errorMessage.includes('auth/network-request-failed')) {
                    errorMessage = 'Internet connection not available.';
                }
                
                throw new Error(errorMessage);
            }
            throw err;
        }
    }

    public async dumpUserClaims(): Promise<void> {
        const user = this.getCurrentUser();
        if (!user) return;
        
        const token = await user.getIdTokenResult();
        const claims = token.claims;
    }

    public async loginWithUserToken(token: string): Promise<TLoginCredentials> {
        const userCred = await signInWithCustomToken(this.auth, token);
        const user = userCred.user;
        await this.dumpUserClaims();
        const cred: TLoginCredentials = { 
            'email': user.email || '', 
            'userUid': user.uid 
        };
        return cred;
    }

    public async logout(): Promise<void> {
        await signOut(this.auth);
    }

    public async resetPassword(email: string): Promise<void> {
        return sendPasswordResetEmail(this.auth, email);
    }

    public async createUserWithEmailAndPassword(email: string, password: string): Promise<string> {
        const userCred = await createUserWithEmailAndPassword(this.auth, email, password);
        const uid = userCred?.user?.uid;
        if (!uid) {
            throw new Error('User ID not found');
        }
        return uid;
    }

    public async registerUser(email: string, password: string) {
        // Using secondary app to avoid logging out current user
        const secondaryAuth = this.getSecondaryAuth();
        const authData = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        await signOut(secondaryAuth);
        return authData;
    }

    public async isEmailAddressAvailable(email: string): Promise<boolean> {
        try {
            const methods = await fetchSignInMethodsForEmail(this.auth, email);
            return methods.length === 0;
        } catch (err) {
            throw new Error(`Error checking email availability: ${err}`);
            // return false;
        }
    }

    public getCurrentUser(): User | null {
        const isOnline = this.connectivityService.isOnline();
        if (!isOnline) {
            return null;
        }
        return this.auth.currentUser;
    }

    public isAuthenticated(): boolean {
        const user = this.getCurrentUser();
        return user !== null;
    }

    public isUserLoggedIn(): boolean {
        return this.isAuthenticated();
    }

    public async isUserPersistentlyAuthenticated(): Promise<boolean> {
        return this.auth.currentUser !== null;
    }

    public async sendPasswordResetEmail(email: string): Promise<boolean> {
        await sendPasswordResetEmail(this.auth, email);
        return true;
    }

    public myOnAuthStateChanged(user: User | null) {
        if (user === undefined || user === null) {
            // User logged out or not authenticated
            return;
        }

        // Auth state changed:', user.email
        switch (this.currentMode) {
            case this.AuthMode.AUTHENTICATE_LOGIN_MODE:
                // User authenticated successfully
                break;
            // ... other cases remain the same
        }
    }

    public async authenticateLogin(email: string, password: string): Promise<void> {
        // Just use the login method directly
        this.currentMode = this.AuthMode.AUTHENTICATE_LOGIN_MODE;
        await this.login(email, password);
    }

    // private async reauthenticateLogin(email: string, password: string): Promise<void> {
    //     this.currentMode = this.AuthMode.REAUTHENTICATE_LOGIN_MODE;
    //     return this.authenticate(this.AuthMode.REAUTHENTICATE_LOGIN_MODE, email, password);
    // }

    public async authenticate(mode: number, email: string, password: string): Promise<void> {
        // First log this user out
        await this.logout();
        this.currentMode = mode;
        await signInWithEmailAndPassword(this.auth, email, password);
    }
}